import { COMPLETENESS_CHECKS, type CompletenessChecks, type CompletenessMeasure, checkCompleteness, createDomMeasure } from "./completeness";
import { HARNESS_LIMITS, type HarnessLimits, type HarnessReportedFailureKind, type HarnessSetup, type HostToHarnessMessage } from "./harnessProtocol";
import { type PageMarginsPx, pageBoxPx, pageLengthPx, pagePaperWidthPx, printableWidthPx } from "./printablePage";

/**
 * The sandbox page a template runs in — built as an `<iframe srcdoc>` string.
 *
 * Two cages. The template's `render(data)` runs in a Web Worker built from a Blob, terminated after the
 * time limit, so an endless loop never freezes the app. Its HTML is shown in an iframe with an opaque
 * origin (no `allow-same-origin`: no cookies, storage, parent DOM or API) under the CSP below:
 * - no `connect-src`: fetch, XHR and WebSocket are blocked, from the worker too (it inherits the policy);
 * - scripts only with this page's nonce (and the worker's `blob:`), so inline event handlers in the
 *   template's output never run;
 * - no `font-src`: the app's own CSP blocks `data:` fonts in this frame anyway, so system fonts only.
 * The app's CSP applies on top of this one; a request either policy blocks is blocked.
 *
 * A sandboxed frame may still navigate ITSELF, and a page it lands on keeps the frame's window and "null"
 * origin. So the output loses every attribute that navigates (`sanitizeTemplateDocument`), link clicks are
 * cancelled (`blockLinkNavigation`), and every message back carries the frame's secret token, which a page
 * the frame navigated to never has. The host also treats a second `load` of the frame as navigation.
 */
export const harnessCsp = (nonce: string): string =>
	`default-src 'none'; script-src 'nonce-${nonce}' blob:; worker-src blob:; style-src 'unsafe-inline'; img-src data:`;

const BASE_STYLE = 'html,body{margin:0}body{font-family:system-ui,-apple-system,"Segoe UI",Roboto,Arial,sans-serif}';

/**
 * Strips a parsed (inert) template page of everything that acts on insertion or leaves the page: elements that
 * load, run or refresh (a meta refresh would navigate the frame off-site), SVG animations (they can set an
 * `href`), and every attribute that navigates or pings — `href` on any element (`<a>`, `<area>`, SVG and MathML),
 * `xlink:href`, `ping`, `action`, `formaction`, `srcdoc`. SELF-CONTAINED (embedded with `String(fn)`).
 */
export function sanitizeTemplateDocument(parsed: Document): void {
	const BLOCKED_ELEMENTS = "script,meta,base,link,iframe,frame,frameset,object,embed,form,portal,fencedframe,animate,set";
	const NAVIGATING_ATTRIBUTES = ["href", "xlink:href", "ping", "action", "formaction", "srcdoc"];
	for (const element of Array.from(parsed.querySelectorAll(BLOCKED_ELEMENTS))) element.remove();
	for (const element of Array.from(parsed.querySelectorAll("*"))) {
		for (const attribute of Array.from(element.attributes)) {
			if (NAVIGATING_ATTRIBUTES.includes(attribute.name.toLowerCase())) element.removeAttributeNode(attribute);
		}
	}
}

/** Cancels any click that would follow a link, in the capture phase so the page can't get in first. SELF-CONTAINED. */
export function blockLinkNavigation(win: Window): void {
	const cancelLink = (event: Event): void => {
		const target = event.target;
		// Only an Element has `closest`, which the `in` check makes sure of.
		if (target !== null && "closest" in target && (target as Element).closest("a, area") !== null) event.preventDefault();
	};
	for (const type of ["click", "auxclick"]) win.addEventListener(type, cancelLink, { capture: true });
	win.addEventListener("submit", (event) => event.preventDefault(), { capture: true });
}

/** A run's result: the page's HTML, or why there is none. */
export type WorkerOutcome = { ok: true; html: string } | { ok: false; kind: HarnessReportedFailureKind; message: string };

/**
 * Runs a template's `render(data)` in a Web Worker built from a Blob and reports exactly once — unless stopped
 * first with the returned function. Kills the worker after the time limit. Anything the worker sends that isn't
 * one of the wrapper's messages counts as bad output. SELF-CONTAINED (embedded with `String(fn)`).
 */
export function runTemplateInWorker(source: string, data: unknown, limits: HarnessLimits, report: (outcome: WorkerOutcome) => void): () => void {
	// The wrapper checks the result inside the worker, so a non-string or oversized one is never cloned to the page.
	const wrapper = `
;self.onmessage = function (event) {
	var output;
	try { output = render(event.data); }
	catch (error) { self.postMessage({ ok: false, kind: "runtime", message: String(error && error.message ? error.message : error) }); return; }
	if (typeof output !== "string") { self.postMessage({ ok: false, kind: "bad-output", message: "render(data) must return a string" }); return; }
	if (output.length > ${limits.maxOutputBytes}) { self.postMessage({ ok: false, kind: "too-large", message: "The page is over ${limits.maxOutputBytes} bytes" }); return; }
	self.postMessage({ ok: true, html: output });
};`;
	const isWrapperKind = (kind: unknown): kind is "runtime" | "bad-output" | "too-large" => kind === "runtime" || kind === "bad-output" || kind === "too-large";
	const parse = (message: unknown): WorkerOutcome => {
		if (typeof message === "object" && message !== null) {
			const { ok, html, kind, message: text } = message as Record<string, unknown>; // a non-null object; each field is checked below
			if (ok === true && typeof html === "string") return { ok, html };
			if (ok === false && isWrapperKind(kind) && typeof text === "string") return { ok, kind, message: text };
		}
		return { ok: false, kind: "bad-output", message: "The template sent something other than its page" };
	};
	const url = URL.createObjectURL(new Blob([source + wrapper], { type: "text/javascript" }));
	const worker = new Worker(url);
	let finished = false;
	const finish = (outcome: WorkerOutcome | null): void => {
		if (finished) return;
		finished = true;
		clearTimeout(timer);
		worker.terminate();
		URL.revokeObjectURL(url);
		if (outcome !== null) report(outcome);
	};
	const timer = setTimeout(
		() => finish({ ok: false, kind: "timeout", message: `render(data) took over ${limits.renderTimeoutMs} ms` }),
		limits.renderTimeoutMs,
	);
	worker.onmessage = (event: MessageEvent<unknown>) => finish(parse(event.data));
	worker.onerror = (event: ErrorEvent) => {
		event.preventDefault();
		finish({ ok: false, kind: /SyntaxError/.test(event.message) ? "syntax" : "runtime", message: event.message });
	};
	worker.postMessage(data);
	return () => finish(null);
}

/** The host's message, checked field by field, or null when it isn't one. SELF-CONTAINED. */
export function readHostMessage(value: unknown): HostToHarnessMessage | null {
	if (typeof value !== "object" || value === null) return null;
	const { type, runId, source, data, required, dir, title } = value as Record<string, unknown>; // a non-null object; each field is checked below
	if (type === "print") return typeof title === "string" ? { type, title } : null;
	const isRequired = Array.isArray(required) && required.every((item) => typeof item?.id === "string" && typeof item?.name === "string");
	if (type !== "render" || typeof runId !== "number" || typeof source !== "string" || !isRequired || (dir !== "ltr" && dir !== "rtl")) return null;
	return { type, runId, source, data, required, dir };
}

/**
 * Puts a template's page in `doc` for checking and printing: parsed inert, sanitized, in the given direction,
 * and laid out at its paper's printable width (see `printableWidthPx`). Returns that width. SELF-CONTAINED.
 */
export function showTemplatePage(doc: Document, html: string, dir: string, tools: Pick<HarnessTools, "sanitize" | "printableWidth">): number {
	const parsed = new DOMParser().parseFromString(html, "text/html");
	tools.sanitize(parsed);
	doc.documentElement.dir = dir;
	doc.body.replaceChildren(...Array.from(parsed.head.childNodes), ...Array.from(parsed.body.childNodes));
	// Read after the page's own styles are in, since its @page rules decide the width.
	const widthPx = tools.printableWidth(doc);
	doc.documentElement.style.setProperty("width", `${widthPx}px`, "important");
	return widthPx;
}

/**
 * Prints the page with no page margin. Browsers draw their header and footer — date, title, the frame's
 * `about:srcdoc` address, page numbers — inside the page margin, so with none there is nowhere to draw them. The
 * template's own margins become the body's padding instead, repeated on every printed page (`box-decoration-break:
 * clone`), and the root grows by the side margins so the content keeps the printable width it was checked at.
 * Everything is put back after printing. The page takes `title` as its title, which browsers suggest as the file
 * name when saving as PDF. SELF-CONTAINED.
 */
export function printWithoutBrowserHeaders(win: Window, margins: PageMarginsPx, title: string): void {
	const doc = win.document;
	doc.title = title;
	const root = doc.documentElement;
	const checkedWidth = root.style.getPropertyValue("width");
	const style = doc.createElement("style");
	// Last in the page and `!important`, so it outranks the template's own `@page` margins and body padding.
	style.textContent = `@page{margin:0 !important}body{box-sizing:border-box;padding:${margins.top}px ${margins.right}px ${margins.bottom}px ${margins.left}px !important;box-decoration-break:clone;-webkit-box-decoration-break:clone}`;
	doc.body.append(style);
	root.style.setProperty("width", `${Number.parseFloat(checkedWidth) + margins.left + margins.right}px`, "important");
	win.addEventListener(
		"afterprint",
		() => {
			style.remove();
			root.style.setProperty("width", checkedWidth, "important");
		},
		{ once: true },
	);
	win.print();
}

/** Everything the harness runs, passed in so each piece is embedded on its own. */
export type HarnessTools = {
	check: typeof checkCompleteness;
	checks: CompletenessChecks;
	createMeasure: (win: Window) => CompletenessMeasure | null;
	showPage: typeof showTemplatePage;
	/** The page's printable width: `printableWidthPx` of `pageBoxPx` with its readers. */
	printableWidth: (doc: Document) => number;
	/** The page's `@page` margins: `pageBoxPx` with its readers. */
	pageMargins: (doc: Document) => PageMarginsPx;
	print: typeof printWithoutBrowserHeaders;
	sanitize: typeof sanitizeTemplateDocument;
	blockNavigation: typeof blockLinkNavigation;
	runInWorker: typeof runTemplateInWorker;
	readMessage: typeof readHostMessage;
};

/**
 * Runs inside the iframe. SELF-CONTAINED (embedded with `String(fn)`): everything else arrives as `tools`.
 * The template's output is parsed inert and sanitized, then shown — so `<script>` never runs — and laid out
 * at the paper's printable width before the completeness check. Print is honoured only after a passing render.
 */
export function harnessMain(setup: HarnessSetup, tools: HarnessTools): void {
	const { limits, token } = setup;
	let stopRun: (() => void) | null = null;
	let lastRenderPassed = false;
	const post = (message: object): void => window.parent.postMessage({ ...message, token }, "*");
	const fail = (runId: number, kind: HarnessReportedFailureKind, message: string): void => post({ type: "failed", runId, kind, message });
	type Request = { runId: number; source: string; data: unknown; required: { id: string; name: string }[]; dir: string };
	const display = (request: Request, html: string, pageMeasure: CompletenessMeasure, startedAt: number): void => {
		// UTF-8 takes at least one byte per UTF-16 unit, so an over-long string is over the limit without encoding it.
		if (html.length > limits.maxOutputBytes || new TextEncoder().encode(html).length > limits.maxOutputBytes) {
			fail(request.runId, "too-large", `The page is over ${limits.maxOutputBytes} bytes`);
			return;
		}
		const widthPx = tools.showPage(document, html, request.dir, tools);
		const report = tools.check(document, request.required, pageMeasure, tools.checks);
		lastRenderPassed = report.ok;
		post({ type: "rendered", runId: request.runId, report, renderMs: performance.now() - startedAt, heightPx: document.documentElement.scrollHeight, widthPx });
	};
	const showOrFail = (request: Request, html: string, pageMeasure: CompletenessMeasure, startedAt: number): void => {
		try {
			display(request, html, pageMeasure, startedAt);
		} catch (error) {
			fail(request.runId, "runtime", String(error));
		}
	};
	const render = (request: Request): void => {
		if (stopRun !== null) stopRun();
		lastRenderPassed = false;
		const startedAt = performance.now();
		const measure = tools.createMeasure(window);
		if (measure === null) fail(request.runId, "unsupported", "This browser has no Element.checkVisibility, so the page can't be checked");
		else if (new TextEncoder().encode(request.source).length > limits.maxSourceBytes) {
			fail(request.runId, "too-large", `The template is over ${limits.maxSourceBytes} bytes`);
		} else {
			stopRun = tools.runInWorker(request.source, request.data, limits, (outcome) => {
				stopRun = null;
				if (!outcome.ok) fail(request.runId, outcome.kind, outcome.message);
				else showOrFail(request, outcome.html, measure, startedAt);
			});
		}
	};

	tools.blockNavigation(window);
	window.addEventListener("message", (event: MessageEvent<unknown>) => {
		const message = event.source === window.parent ? tools.readMessage(event.data) : null;
		if (message?.type === "render") render(message);
		else if (message?.type === "print" && lastRenderPassed) tools.print(window, tools.pageMargins(document), message.title);
		// Refused: the host still learns the request is over.
		else if (message?.type === "print") post({ type: "print-ended" });
	});
	window.addEventListener("afterprint", () => post({ type: "print-ended" }));
	window.addEventListener("pagehide", () => post({ type: "leaving" }));
	post({ type: "ready" });
}

const PAGE_READERS_SOURCE = `{ lengthPx: ${String(pageLengthPx)}, paperWidthPx: ${String(pagePaperWidthPx)} }`;

const HARNESS_TOOLS_SOURCE = `{
	check: ${String(checkCompleteness)},
	checks: { findNameRuns: ${String(COMPLETENESS_CHECKS.findNameRuns)}, isNameShown: ${String(COMPLETENESS_CHECKS.isNameShown)}, hasMediaRules: ${String(COMPLETENESS_CHECKS.hasMediaRules)} },
	createMeasure: ${String(createDomMeasure)},
	showPage: ${String(showTemplatePage)},
	printableWidth: (doc) => (${String(printableWidthPx)})((${String(pageBoxPx)})(doc, ${PAGE_READERS_SOURCE})),
	pageMargins: (doc) => (${String(pageBoxPx)})(doc, ${PAGE_READERS_SOURCE}).margins,
	print: ${String(printWithoutBrowserHeaders)},
	sanitize: ${String(sanitizeTemplateDocument)},
	blockNavigation: ${String(blockLinkNavigation)},
	runInWorker: ${String(runTemplateInWorker)},
	readMessage: ${String(readHostMessage)},
}`;

/** The secrets one frame is built with, fresh per frame: the CSP script nonce and the message token. */
export type HarnessFrameSecrets = { nonce: string; token: string };

/** The full srcdoc for one frame. */
export const buildHarnessSrcdoc = ({ nonce, token }: HarnessFrameSecrets): string => {
	const setup: HarnessSetup = { limits: HARNESS_LIMITS, token };
	return `<!doctype html><html><head><meta charset="utf-8"><meta http-equiv="Content-Security-Policy" content="${harnessCsp(nonce)}"><style>${BASE_STYLE}</style><script nonce="${nonce}">(${String(harnessMain)})(${JSON.stringify(setup)}, ${HARNESS_TOOLS_SOURCE});</script></head><body></body></html>`;
};
